import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Paperclip, Send } from "lucide-react";

const STATUS_LABEL = {
  // Spec utilisateur fork 25.14 — sémantique alignée sur l'expérience
  // visiteur/établissement :
  //   • « open » dans le backend = ticket vient d'être créé, en
  //     attente d'une 1ʳᵉ réponse du support → label « En attente ».
  //   • « pending » = le support a répondu, on attend une éventuelle
  //     relance du propriétaire (sinon clôture auto à 72h) → label
  //     « Ouverte ».
  //   • « closed » = clôturée (manuel ou auto 72h).
  open: { label: "En attente", cls: "bg-brand/10 text-brand" },
  pending: { label: "Ouverte", cls: "bg-[#ECFBEF] text-[#0A7F2E]" },
  closed: { label: "Clôturée", cls: "bg-destructive/10 text-destructive" },
};

export default function TicketDetail({ admin = false }) {
  const { id } = useParams();
  const nav = useNavigate();
  const { role, user, loading: authLoading } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Guard: non-admin visitor view is reserved for accounts with a real
  // identity (establishment, admin or a non-guest visitor). Anonymous
  // guests are redirected — they follow up by email only.
  useEffect(() => {
    if (admin || authLoading) return;
    const allowed = role === "commerce" || (role === "client" && !user?.isGuest);
    if (!allowed) {
      nav("/", { replace: true });
    }
  }, [admin, authLoading, role, user, nav]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/support/tickets/${id}`);
      setTicket(data);
    } catch (err) {
      if (err.response?.status === 401) nav("/login");
      else if (err.response?.status === 403) { toast.error("Accès refusé"); nav(admin ? "/admin/support" : "/my-requests"); }
      else if (err.response?.status === 404) { toast.error("Demande introuvable"); nav(admin ? "/admin/support" : "/my-requests"); }
    } finally {
      setLoading(false);
    }
  }, [id, nav, admin]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (reply.trim().length < 2) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${id}/replies`, { body: reply });
      setReply("");
      toast.success("Réponse envoyée");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/support/tickets/${id}/status`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const downloadAttachment = async () => {
    try {
      const { data } = await api.get(`/support/tickets/${id}/attachment`);
      const bin = atob(data.dataBase64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: data.mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = data.name || "attachment"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur téléchargement");
    }
  };

  if (loading || !ticket) return <div className="container mx-auto py-16 text-center text-subtle">Chargement…</div>;

  const st = STATUS_LABEL[ticket.status] || STATUS_LABEL.open;

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 max-w-3xl">
      <Link to={admin ? "/admin/support" : "/my-requests"} className="text-sm text-subtle hover:text-ink" data-testid="back-link">
        ← {admin ? "Toutes les demandes" : "Mes demandes"}
      </Link>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <span className="font-mono text-xs text-subtle" data-testid="tracking-number">{ticket.trackingNumber}</span>
        <span className={`inline-block px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${st.cls}`} data-testid="ticket-status">
          {st.label}
        </span>
      </div>
      <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-ink mt-2">{ticket.subject}</h1>
      <p className="mt-1 text-sm text-subtle">
        {new Date(ticket.createdAt).toLocaleString("fr-FR")}
        {admin && <> · <strong>{ticket.userName}</strong> ({ticket.email}) · {ticket.userType === "client" ? "Visiteur" : "Établissement"}</>}
      </p>

      {admin && (
        <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="admin-actions">
          <span className="text-xs uppercase font-bold tracking-wider text-subtle">Statut :</span>
          {["open", "pending", "closed"].map((s) => (
            <button key={s} onClick={() => setStatus(s)} disabled={ticket.status === s} className={`text-xs font-medium px-3 h-8 border ${ticket.status === s ? "border-ink bg-ink text-white" : "border-border text-subtle hover:border-ink"}`} data-testid={`set-status-${s}`}>
              {STATUS_LABEL[s].label}
            </button>
          ))}
        </div>
      )}

      {/* Question initiale — bulle alignée à GAUCHE (style "question"). */}
      <div className="mt-8 max-w-[90%] md:max-w-[80%] mr-auto">
        <div className="border border-border p-4 md:p-5 bg-surface/40 rounded-lg">
          <p className="text-xs font-bold uppercase tracking-wider text-subtle">
            {ticket.userType === "client"
              ? `Question de ${ticket.userName || "visiteur"}`
              : `Question de ${ticket.userName || "l'établissement"}`}
            {" · "}
            <span className="font-normal normal-case tracking-normal">{new Date(ticket.createdAt).toLocaleString("fr-FR")}</span>
          </p>
          <div className="mt-3 whitespace-pre-wrap text-ink leading-relaxed">{ticket.message}</div>
          {ticket.hasAttachment && (
            <div className="mt-4 pt-4 border-t border-border flex items-center gap-2">
              <Paperclip size={16} className="text-subtle" />
              <span className="text-sm text-ink flex-1">
                {ticket.attachment?.name || "Pièce jointe"}{" "}
                <span className="text-subtle">({Math.round((ticket.attachment?.size || 0) / 1024)} Ko)</span>
              </span>
              {admin && (
                <Button size="sm" variant="outline" onClick={downloadAttachment} className="rounded-md" data-testid="download-attachment-btn">Télécharger</Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Accusé de réception automatique — bulle SUPPORT (bleutée, alignée
         à droite) générée côté UI au moment où la question est créée. Ce
         n'est PAS un reply réel en base : c'est un message système pour
         informer immédiatement l'utilisateur que sa demande est enregistrée.
         Si l'équipe support répond ensuite, ses réponses s'enchaînent juste
         APRÈS cette bulle (ordre chronologique naturel). */}
      <div className="mt-4 max-w-[90%] md:max-w-[80%] ml-auto" data-testid="auto-ack">
        <div className="p-4 md:p-5 rounded-lg border border-brand/40 bg-[#EEF3FF]">
          <p className="text-xs font-bold uppercase tracking-wider text-subtle">
            <span className="text-brand">Support</span>
            {" · "}
            <span className="font-normal normal-case tracking-normal">{new Date(ticket.createdAt).toLocaleString("fr-FR")}</span>
          </p>
          <div className="mt-3 text-ink leading-relaxed">
            Votre demande a bien été enregistrée. <strong>Vous serez notifié(e) par email</strong> dès qu'une réponse sera apportée par notre équipe. Vous pourrez également la consulter ici-même, dans <strong>Mes demandes</strong>.
          </div>
        </div>
      </div>

      {/* Réponses — affichage différencié selon le point de vue (spec
         fork 25.13).
         - Côté visiteur/établissement (`admin=false`) : la zone propre
           du propriétaire du ticket est la **question originale** déjà
           rendue plus haut dans son propre bandeau (« QUESTION DE …»).
           Tout ce qui apparaît dans `replies[]` constitue donc, du
           point de vue du propriétaire, une intervention du support
           BypassGo (admin réel OU agent support intervenant depuis un
           autre compte technique). On unifie tous ces messages sous
           l'étiquette « Réponse du support », bulle bleutée alignée
           à droite — quel que soit l'`authorRole` ou l'`authorName`
           stocké en base.
         - Côté admin (`admin=true`) dans la console support : on
           conserve l'attribution technique (nom réel + rôle) car
           l'équipe support a besoin de tracer qui a écrit quoi
           lors d'une enquête. */}
      {(ticket.replies || []).map((r) => {
        const isAdmin = r.authorRole === "admin";
        // Côté propriétaire : toujours « Réponse du support ».
        // Côté admin : « Support » si admin a écrit, sinon nom réel.
        const showAsSupport = admin ? isAdmin : true;
        const label = showAsSupport
          ? "Réponse du support"
          : (r.authorName || "Utilisateur");
        return (
          <div
            key={r.id}
            className={`mt-4 max-w-[90%] md:max-w-[80%] ${showAsSupport ? "ml-auto" : "mr-auto"}`}
            data-testid={`reply-${r.id}`}
          >
            <div
              className={`p-4 md:p-5 rounded-lg border ${
                showAsSupport
                  ? "border-brand/40 bg-[#EEF3FF]"
                  : "border-border bg-surface/40"
              }`}
            >
              <p className="text-xs font-bold uppercase tracking-wider text-subtle">
                {showAsSupport
                  ? <span className="text-brand">{label}</span>
                  : label}
                {" · "}
                <span className="font-normal normal-case tracking-normal">{new Date(r.createdAt).toLocaleString("fr-FR")}</span>
              </p>
              <div className="mt-3 whitespace-pre-wrap text-ink leading-relaxed">{r.body}</div>
            </div>
          </div>
        );
      })}

      {/* Reply form — spec utilisateur fork 25.14.
          • Côté admin (`admin=true`) : visible tant que le ticket n'est
            pas clos, comme avant.
          • Côté propriétaire (`admin=false`) : visible UNIQUEMENT
            quand le ticket est passé en statut « pending » (= « Ouverte »
            dans le langage utilisateur), c'est-à-dire après que le
            support a répondu. Permet à l'établissement de relancer
            le support directement depuis l'écran. Si la fenêtre de
            72 h se ferme sans nouvelle réponse, l'auto-clôture bascule
            le ticket en « closed » et le formulaire disparaît. */}
      {((admin && ticket.status !== "closed") || (!admin && ticket.status === "pending")) && (
        <form onSubmit={send} className="mt-8" data-testid="reply-form">
          <p className="overline mb-2">
            {admin ? "Répondre" : "Relancer le support"}
          </p>
          <Textarea
            required
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={5}
            className="resize-none"
            placeholder={admin ? "Votre réponse…" : "Votre message complémentaire à l'équipe support…"}
            data-testid="reply-input"
          />
          {!admin && (
            <p className="mt-1.5 text-[11px] text-subtle leading-snug">
              Si vous ne répondez pas dans les 72 h, cette demande sera automatiquement clôturée. Vous pourrez alors en ouvrir une nouvelle si besoin.
            </p>
          )}
          <Button type="submit" disabled={sending} className="mt-3 h-11 bg-brand hover:bg-brand-hover text-white rounded-md" data-testid="reply-submit-btn">
            {sending ? "Envoi…" : <><Send size={14} className="mr-2" /> Envoyer</>}
          </Button>
        </form>
      )}

      {/* Le bandeau « Votre demande a bien été enregistrée… » a été déplacé
         tout en haut sous forme de bulle SUPPORT automatique (auto-ack) :
         on ne le ré-affiche plus ici pour éviter la redondance. */}

      {ticket.status === "closed" && (
        <p className="mt-8 text-sm text-subtle border border-border p-4">
          Cette demande est clôturée. Créez une nouvelle demande si vous avez besoin d'aide.
        </p>
      )}
    </div>
  );
}
